/**
 * Compresses an image using canvas and returns the compressed image data
 * @param {HTMLImageElement} image - The original image element
 * @param {number} quality - The quality level (1-100)
 * @param {HTMLCanvasElement} canvas - The canvas element to use for compression
 * @returns {Promise<Object>} - Promise resolving to an object with the compressed image URL and size
 */
export const compressImage = (image, quality, canvas) => {
  return new Promise((resolve, reject) => {
    try {
      // Set canvas dimensions to match image
      canvas.width = image.width;
      canvas.height = image.height;
      
      // Draw image onto canvas
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      
      // Convert to data URL with specified quality
      const mimeType = 'image/jpeg';
      const dataUrl = canvas.toDataURL(mimeType, quality / 100);
      
      // Estimate file size from data URL
      const base64Data = dataUrl.split(',')[1];
      const decodedData = atob(base64Data);
      const size = decodedData.length;
      
      // Get the original image size from data URL
      const originalSize = getOriginalImageSize(image.src);
      
      // If compressed size is larger than original when at 100% quality,
      // use the original size instead to prevent size increase
      if (quality === 100 && size > originalSize && originalSize > 0) {
        resolve({
          url: dataUrl,
          size: originalSize
        });
      } else {
        resolve({
          url: dataUrl,
          size: size
        });
      }
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Extracts size information from base64 data URL
 * @param {string} dataUrl - The data URL
 * @returns {number} - The approximate size in bytes
 */
export const getDataUrlSize = (dataUrl) => {
  const base64Data = dataUrl.split(',')[1];
  const decodedData = atob(base64Data);
  return decodedData.length;
};

/**
 * Gets the size of the original image from its data URL
 * @param {string} dataUrl - The data URL of the original image
 * @returns {number} - The approximate size in bytes
 */
export const getOriginalImageSize = (dataUrl) => {
  if (!dataUrl || !dataUrl.startsWith('data:')) {
    return 0;
  }
  return getDataUrlSize(dataUrl);
}; 